import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { 
    Territory, 
    TerritoryWithDetails, 
    CreateTerritoryRequest, 
    UpdateTerritoryRequest, 
    AssignUsersRequest,
    TerritoryFilters 
} from '../models/territory.model';

export interface TerritoryItem {
  _id: string;
  name: string;
  status: string;
}

export interface GroupedTerritories {
  [region: string]: TerritoryItem[];
}

@Injectable({
    providedIn: 'root'
})
export class TerritoryService {
    private apiUrl = `${environment.apiUrl}/territories`;

    constructor(private http: HttpClient) {}

    // Create a new territory
    createTerritory(data: CreateTerritoryRequest): Observable<{ message: string; data: Territory }> {
        return this.http.post<{ message: string; data: Territory }>(this.apiUrl, data);
    }

    // Get territories by subscriber
    getTerritoriesBySubscriber(filters?: TerritoryFilters): Observable<{ message: string; data: Territory[] }> {
        let params = '';
        if (filters) {
            const queryParams = new URLSearchParams();
            if (filters.status) queryParams.append('status', filters.status);
            if (filters.region) queryParams.append('region', filters.region);
            if (filters.searchText) queryParams.append('searchText', filters.searchText);
            params = queryParams.toString();
        }
        
        const url = params ? `${this.apiUrl}?${params}` : this.apiUrl;
        return this.http.get<{ message: string; data: Territory[] }>(url);
    }

    // Get territories with user details
    getTerritoriesWithUserDetails(): Observable<{ message: string; data: TerritoryWithDetails[] }> {
        return this.http.get<{ message: string; data: TerritoryWithDetails[] }>(`${this.apiUrl}/with-users`);
    }

    // Get territory by ID
    getTerritoryById(id: string): Observable<{ message: string; data: TerritoryWithDetails }> {
        return this.http.get<{ message: string; data: TerritoryWithDetails }>(`${this.apiUrl}/${id}`);
    }

    // Update territory
    updateTerritory(id: string, data: UpdateTerritoryRequest): Observable<{ message: string; data: Territory }> {
        return this.http.put<{ message: string; data: Territory }>(`${this.apiUrl}/${id}`, data);
    }

    // Delete territory
    deleteTerritory(id: string): Observable<{ message: string }> {
        return this.http.delete<{ message: string }>(`${this.apiUrl}/${id}`);
    }

    // Assign users to territory
    assignUsersToTerritory(id: string, data: AssignUsersRequest): Observable<{ message: string; data: Territory }> {
        return this.http.post<{ message: string; data: Territory }>(`${this.apiUrl}/${id}/assign-users`, data);
    }

    // Unassign users from territory
    unassignUsersFromTerritory(id: string, data: AssignUsersRequest): Observable<{ message: string; data: Territory }> {
        return this.http.post<{ message: string; data: Territory }>(`${this.apiUrl}/${id}/unassign-users`, data);
    }

    // Get territories by region
    getTerritoriesByRegion(region: string): Observable<{ message: string; data: Territory[] }> {
        return this.http.get<{ message: string; data: Territory[] }>(`${this.apiUrl}/region/${region}`);
    }

    // Get available users for assignment
    getAvailableUsersForAssignment(): Observable<{ message: string; data: any[] }> {
        return this.http.get<{ message: string; data: any[] }>(`${this.apiUrl}/available-users`);
    }

    getTerritoriesGroupedByRegion(): Observable<GroupedTerritories> {
      return this.http.get<{message: string, data: GroupedTerritories}>(`${environment.apiUrl}/territories/grouped`).pipe(
        map(response => response.data)
      );
    }
} 